import { IBlock } from "../../../framework/src/IBlock";
import { Message } from "../../../framework/src/Message";
import { BlockComponent } from "../../../framework/src/BlockComponent";
import MessageEnum, {
  getName,
} from "../../../framework/src/Messages/MessageEnum";
import { runEngine } from "../../../framework/src/RunEngine";
import * as Yup from "yup";
import copy from 'copy-to-clipboard';
import { SelectChangeEvent } from "@mui/material/Select";
import React, { ChangeEvent } from "react";
import { getStorageData, setStorageData } from "framework/src/Utilities";
import { scrollToTop } from "../../../components/src/utils";
import { InfoType } from "../../../components/src/CustomInfoDialog.web";
import { decodeUserSlug } from "../../../components/src/utils/userSlug.web";

// Customizable Area Start
export interface IAttribute {
  name: string;
  title: string;
  field_type: string;
  is_enable: boolean;
  is_required: boolean;
  value: string | number;
}

export interface PortfolioLinks {
  portfolioLink1: string;
  portfolioLink2: string;
  portfolioLink3: string;
  portfolioLink4: string;
  portfolioLink5?: string;
}

export interface SocialMediaLinks {
  facebookLink?: string;
  instagramLink?: string;
  X?: string;
  youtubeLink?: string;
  linkedinLink?: string;
}

export interface IAttributeValues {
  name: string;
  value: string;
}

type FieldType =
  | "string"
  | "file"
  | "textarea"
  | "text"
  | "date"
  | "datetime"
  | "integer"
  | "number"
  | "boolean"
  | "float"
  | "checkbox";
type FieldValue = string | number | boolean | Date | null;
export interface IField {
  name: string;
  title: string;
  field_type: FieldType;
  is_enable: boolean;
  is_required: boolean;
}

interface IProfileDataWeb {
  attributes?: { user_profile_data: { [key: string]: FieldValue } };
}

export interface IProfileData {
  [key: string]: FieldValue;
}

export interface IProfileValues {
  value: string;
}

export interface IDeleteSkills {
  message: string;
}

export interface IDeleteSkillsError {
  message: string;
}

type SkillLevel = "Beginner" | "Intermediate" | "Expert";

export interface Skill {
  category: string;
  subCategory: string;
  subCategoryId: number;
  level: SkillLevel;
}

type ProfileErrors = {
  [key: string]: string;
};
export interface EducationAttribute {
  institute_name: string;
  qualification: string;
  start_date: string;
  end_date: string;
  start_year: string;
  end_year: string;
  is_ongoing: boolean;
  location: string;
}

export interface Education {
  id: string;
  attributes: EducationAttribute;
}
interface CareerHighlightAttributes {
  career_images_urls?: string;
  project_name?: string;
  role?: string;
  start_date?: string;
  start_year?: string;
  end_date?: string;
  end_year?: string;
  duration?: string;
  location?: string;
  description?: string;
  ongoing: boolean;
  project_link?: string;
}

export interface CareerHighlight {
  id: string;
  attributes: CareerHighlightAttributes;
}
export interface LocationOption {
  key: string;
  value: string;
}

export interface LanguageOption {
  key: string;
  value: string;
}

interface SubCategoryAttributes {
  name: string;
}

interface SubCategoryItem {
  attributes: SubCategoryAttributes;
}

interface SubCategoryResponse {
  data: SubCategoryItem[];
}

export interface CategoryAttributes {
  id: number;
  category_name: string;
  name: string;
}

export interface CategoryItem {
  attributes: CategoryAttributes;
}

export interface CategoryResponse {
  data: CategoryItem[];
}

export interface SubCategory {
  id: number;
  name: string;
  experience_level: string;
}
export interface Category {
  id: number;
  name: string;
  sub_categories: SubCategory[];
}

type FileTypeCategory = "image" | "video" | "audio" | "application/pdf";

const ALLOWED_MEDIA_TYPE = [
  "image/jpeg",
  "image/png",
  "image/svg+xml",
  "video/mp4",
  "video/x-msvideo",
  "video/avi",
  "audio/mpeg",
  "application/pdf",
];

const MAX_MEDIA_SIZES: Record<FileTypeCategory, number> = {
  video: 104857600,
  "application/pdf": 10485760,
  image: 10485760,
  audio: 10485760,
};

const MAX_LIMITS: Record<FileTypeCategory, number> = {
  image: 20,
  "application/pdf": 5,
  audio: 5,
  video: 5,
};

export type PortfolioItem = {
  id: number;
  type: "image" | "video" | "audio" | "application/pdf";
  url: string;
};

interface Language {
  id: number | string;
  name: string;
}

export interface ClickableImageProps {
  src: string | undefined;
  borderRadius?: string;
  onClick?: () => void;
}

interface IProfileAttributes {
  activated: boolean;
  country_code: string;
  email: string;
  first_name: string;
  full_phone_number: string;
  last_name: string;
  height: number | null;
  weight: number | null;
  roles: string;
  blocked: boolean;
  type: string | null;
  status: string;
  role_id: number | null;
  gender: string | null;
  full_name: string;
  age: number | null;
  date_of_birth: string | null;
  locations: string;
  languages: string[];
  description: string;
  created_at: string;
  updated_at: string;
  unique_auth_id: string;
  social_media_links: Record<string, any>;
  user_careers: CareerHighlight[];
  categories: Category[];
  user_educations: Education[];
  user_links: Record<string, any>;
  user_image_url: string;
  cover_photo: string;
  upload_media: PortfolioItem[];
}

interface ICreateProfileSuccessResponse {
  data: {
    id: string;
    type: string;
    attributes: IProfileAttributes;
  };
}

interface IDeleteProfileSuccessResponse {
  data: {
    [key: string]: string;
  };
}

interface IDeletePortfolioSuccessResponse {
  [key: string]: string;
}

interface ICreateProfileErrorResponse {
  errors: {
    [key: string]: string;
  }[];
}

const DEFAULT_STATE: S = {
  profile: {},
  profileImageUrl: "",
  fields: [],
  attributes: [],
  attributesValues: [],
  txtInputValue: "",
  txtSavedValue: "A",
  enableField: false,
  first_name: "",
  lastName: "",
  fullName: "",
  email: "",
  city: "",
  address: "",
  postalCode: "",
  profilePicture: "",
  profileRole: "jobseeker",
  aboutMe: "",
  gender: "",
  user_name: "",
  dateOfBirth: "",
  biography: "",
  currentPassword: "",
  newPassword: "",
  reNewPassword: "",
  youtube: "",
  instagram: "",
  facebook: "",
  qrCode: "",
  profileId: "",
  countryOpen: false,
  user_type: "",
  imageModalVisible: false,
  userAge: 0,
  phoneNumber: "",
  passwordHelperText: "",
  enablePasswordField: true,
  enableReTypePasswordField: true,
  enableNewPasswordField: true,
  video: "",

  edtEmailEnabled: true,
  llDoChangePwdContainerVisible: false,
  llChangePwdDummyShowContainerVisible: false,
  isDatePickerVisible: false,

  edtMobileNoEnabled: true,
  countryCodeEnabled: true,
  saveButtonDisable: false,

  isProfileModalOpen: false,
  isCoverPhotoModalOpen: false,
  iseditPortfolioImgOpen: false,
  isdeletePortfolioImgOpen: false,
  isEditAboutOpen: false,
  isCareerModalOpen: false,
  editedAbout: "",
  careerHighlights: [],
  educationDetails: [],
  image: null,
  profileImgPreview: null,
  selectedProfileImagePreview: "",
  selectedCoverImagePreview: "",
  getProfile: null,
  coverImgPreview: null,
  currentFile: null,
  portfolioImgs: [],
  currentImgIndex: null,
  deleteItemIndex: null,
  isEducationModalOpen: false,
  isPortfolioModalOpen: false,
  isSocialMediaModalOpen: false,
  selectedCareerHighlight: null,
  isEditingCareerHighlight: false,
  isProfessionalModalOpen: false,
  isDeleteSkillModal: false,
  skillToDeleteIndex: null,
  skillToDeleteId: null,
  selectedCategory: "",
  selectedSubCategory: [],
  isDeleteCareerHighlightModal: false,
  selectedCareerHighlightId: "",
  selectedCareerHighlightIndex: -1,
  selectedEducationIndex: -1,
  selectedEducationId: "",
  isDeleteEducationModal: false,
  languageOptions: [],
  selectedSkills: [],
  categories: [],
  categoriesList: [],
  subCategories: [],
  savedSkills: [],
  selectedEducation: null,
  isEditingEducation: false,
  locationOptions: [],
  portfolioLinks: {
    portfolioLink1: "",
    portfolioLink2: "",
    portfolioLink3: "",
    portfolioLink4: "",
    portfolioLink5: "",
  },
  facebookLink: "",
  instagramLink: "",
  X: "",
  youtubeLink: "",
  linkedinLink: "",
  isBasicInfoModalOpen: false,
  isLoading: false,
  profileData: {
    firstName: "",
    lastName: "",
    location: "",
    language: [],
    height: 0,
    weight: 0,
    age: 0,
    userGender: "",
    role: "",
    selectedLanguages: [],
  },
  tempProfileData: {
    firstName: "",
    lastName: "",
    location: "",
    language: [],
    height: 0,
    weight: 0,
    age: 0,
    userGender: "",
    role: "",
    selectedLanguages: [],
  },
  errors: {},
  selectedCheckbox: false,
  socialLinks: {
    facebookLink: "",
    instagramLink: "",
    X: "",
    youtubeLink: "",
    linkedinLink: "",
  },
  isMediaModalOpen: false,
  expandedMedia: "",
  isUploadPortfolioOpen: false,
  progress: 0,
  remainingTime: 30,
  isUploading: false,
  isPaused: false,
  isUploadSuccess: false,
  isMediaUploadSuccessModalOpen: false,
  validPortfolioError: [],
  selectedPortfolioImageIndex: null,
  selectedPortfolioImage: null,
  imageError: "",
  aboutDetails: "",
  selectedUserId: "",
  loggedInUserId: "",
  isInfoDialog: false,
  infoMessage: "",
  infoType: "success",
  isPortfolioDialog: false,
  initialPortfolioIndex: 0,
};
// Customizable Area End

export const configJSON = require("./config");

export interface Props {
  navigation: any;
  id: string;
  // Customizable Area Start
  route: {
    params: {
      path: {
        username: string | undefined;
      };
    };
  };
  // Customizable Area End
}

interface S {
  // Customizable Area Start
  profile: { [key: string]: FieldValue };
  fields: IField[];
  currentProfile?: { [key: string]: FieldValue };
  profileImageUrl: string;
  saveEnabled?: boolean;
  cancelEnabled?: boolean;

  txtInputValue: string;
  txtSavedValue: string;

  first_name: string;
  lastName: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  city: string;
  address: string;
  attributesValues: IProfileValues[];
  countryOpen: boolean;
  postalCode: string;
  profilePicture: string;
  profileRole: string;
  aboutMe: string;
  gender: string;
  user_name: string;
  dateOfBirth: string;
  currentPassword: string;
  newPassword: string;
  reNewPassword: string;
  instagram: string;
  facebook: string;
  youtube: string;
  qrCode: string;
  profileId: string;
  user_type: string;
  imageModalVisible: boolean;
  userAge: number;
  biography: string;
  video: string;
  attributes: IAttribute[];

  passwordHelperText: string;
  enablePasswordField: boolean;
  enableReTypePasswordField: boolean;
  enableNewPasswordField: boolean;

  edtEmailEnabled: boolean;
  llDoChangePwdContainerVisible: boolean;
  llChangePwdDummyShowContainerVisible: boolean;
  isDatePickerVisible: boolean;

  edtMobileNoEnabled: boolean;
  countryCodeEnabled: boolean;

  saveButtonDisable: boolean;
  enableField: boolean;

  isProfileModalOpen: boolean;
  isCoverPhotoModalOpen: boolean;
  iseditPortfolioImgOpen: boolean;
  isdeletePortfolioImgOpen: boolean;
  isEditAboutOpen: boolean;
  aboutDetails: string;
  editedAbout: string;
  isCareerModalOpen: boolean;
  careerHighlights: CareerHighlight[];
  educationDetails: Education[];
  image: File | null;
  profileImgPreview: string | null;
  selectedProfileImagePreview: string;
  selectedCoverImagePreview: string;
  getProfile: ICreateProfileSuccessResponse["data"] | null;
  socialLinks: SocialMediaLinks;
  portfolioLinks: PortfolioLinks;
  coverImgPreview: string | null;
  portfolioImgs: PortfolioItem[];
  currentImgIndex: number | null;
  deleteItemIndex: number | null;
  selectedPortfolioImageIndex: number | null;
  selectedPortfolioImage: PortfolioItem | null;
  selectedCareerHighlight: CareerHighlight | null;
  isEditingCareerHighlight: boolean;
  selectedEducation: Education | null;
  isEditingEducation: boolean;
  isDeleteSkillModal: boolean;
  skillToDeleteIndex: number | null;
  skillToDeleteId: number | null;
  isDeleteCareerHighlightModal: boolean;
  selectedCareerHighlightId: string;
  selectedCareerHighlightIndex: number;
  selectedEducationIndex: number;
  selectedEducationId: string;
  isDeleteEducationModal: boolean;
  languageOptions: LanguageOption[];
  // Customizable Area End
  isEducationModalOpen: boolean;
  isPortfolioModalOpen: boolean;
  isSocialMediaModalOpen: boolean;
  facebookLink: string;
  instagramLink: string;
  X: string;
  youtubeLink: string;
  linkedinLink: string;
  isBasicInfoModalOpen: boolean;
  isProfessionalModalOpen: boolean;
  categories: any;
  categoriesList: any;
  subCategories: string[];
  savedSkills: Skill[];
  selectedSkills: Skill[];
  selectedSubCategory: string[];
  selectedCategory: string;
  locationOptions: LocationOption[];
  isLoading: boolean;
  profileData: {
    firstName: string;
    lastName: string;
    location: string;
    height: number | null;
    weight: number | null;
    language: string[];
    age: number | null;
    userGender: any;
    role: string;
    selectedLanguages: string[];
  };
  tempProfileData: {
    firstName: string;
    lastName: string;
    location: string;
    height: number | null;
    weight: number | null;
    language: string[];
    age: number | null;
    userGender: string | null;
    role: string;
    selectedLanguages: string[];
  };
  errors: ProfileErrors;
  selectedCheckbox: boolean;
  isUploadPortfolioOpen: boolean;
  progress: number;
  remainingTime: number;
  isUploading: boolean;
  isPaused: boolean;
  currentFile: File | null;
  isUploadSuccess: boolean;
  isMediaUploadSuccessModalOpen: boolean;
  validPortfolioError: string[];
  imageError: string;
  expandedMedia: string;
  isMediaModalOpen: boolean;
  selectedUserId: string | null;
  loggedInUserId: string | null;
  isInfoDialog: boolean;
  infoType: InfoType;
  infoMessage: string;
  isPortfolioDialog: boolean;
  initialPortfolioIndex: number;
  // Customizable Area End
}

interface SS {
  id: any;
  // Customizable Area Start
  // Customizable Area End
}

export default class CustomisableUserProfilesController extends BlockComponent<
  Props,
  S,
  SS
> {
  // Customizable Area Start
  getCustomizableProfileCallId: string = "";
  getCustomizableProfileFieldsCallId: string = "";
  updateCustomizableProfileCallId: string = "";
  getProfileAccountID: string = "";
  deleteCareerHightlightApiCallId: string = "";
  deleteEducationApiCallId: string = "";
  updateProfileID: string = "";
  getAttributesID: string = "";
  token: string = "";
  intervalId: number = 0;

  labelEmail: string = "";

  btnTextCancelPasswordChange: string = "";
  btnTextSaveChanges: string = "";
  labelHeader: string = "";
  btnTextChangePassword: string = "";

  apiCallMessageUpdateProfileRequestId: string = "";
  validationApiCallId: string = "";
  apiChangePhoneValidation: string = "";
  registrationAndLoginType: string = "";
  authToken: string = "";
  uniqueSessionRequesterId: string = "";
  userProfileGetApiCallId: string = "";
  userProfileUploadApiCallId: string = "";
  userCoverUploadApiCallId: string = "";
  deleteProfilePictureApiCallId: string = "";
  deleteCoverPictureApiCallId: string = "";
  userEditSocialLinkApiCallId: string = "";
  editUserProfileApiCallId: string = "";
  editUserAboutApiCallId: string = "";
  userEditPortfolioLinkApiCallId: string = "";
  getCategoryLIstApiCallId: string = "";
  getUserProfileDetailsApiCallId: string = "";
  getUserProfileLocationApiCallId: string = "";
  getSubCategoryListApiCallId: string = "";
  editProfessionalSkillApiCallId: string = "";
  getLanguageListingApiCallId: string = "";
  deleteSkillsApiCallId: string = "";
  deletePortfolioApiCallId: string = "";
  editPortfolioApiCallId: string = "";
  scrollPosition: number = 0;
  infoDialogCallback: null | (() => void) = null;
  // Customizable Area End
  constructor(props: Props) {
    super(props);
    this.receive = this.receive.bind(this);
    this.handleDeleteImage = this.handleDeleteImage.bind(this);
    this.subScribedMessages = [
      // Customizable Area Start
      getName(MessageEnum.RestAPIResponceDataMessage),
      getName(MessageEnum.SessionSaveMessage),
      getName(MessageEnum.SessionResponseMessage),
      getName(MessageEnum.RestAPIResponceMessage),
      // Customizable Area End
    ];

    this.state = {
      // Customizable Area Start
      ...DEFAULT_STATE,
      // Customizable Area End
    };
    // Customizable Area Start
    // Customizable Area End
    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);
  }

  async componentDidMount() {
    super.componentDidMount();
    this.getToken();
    // Customizable Area Start
    this.loadUserProfile();
    // Customizable Area End
  }

  async componentDidUpdate(prevProps: Props) {
    const prevSlug = prevProps.route?.params?.path?.username;
    const currentSlug = this.props.route.params?.path?.username;

    if (prevSlug !== currentSlug && currentSlug) {
     this.resetState(() => {
      this.loadUserProfile();
    });
    }

    if (this.state.currentProfile) {
      if (
        JSON.stringify(this.state.currentProfile) !==
        JSON.stringify(this.state.profile)
      ) {
        if (!this.state.cancelEnabled) this.setState({ cancelEnabled: true });
        this.checkRequiredFields();
      }
    }
  }

  resetState = (callback?: () => void) => {
  this.setState({ ...DEFAULT_STATE }, callback);
};

  loadUserProfile = () => {
    scrollToTop(true);
    const userSlug = this.props.route.params?.path?.username;

    try {
      let selectedUserId: string | null = "";
      if (userSlug) {
        const { userId } = decodeUserSlug(userSlug);
        selectedUserId = userId;
      }
      const profileDataString = localStorage.getItem(configJSON.PROFILE);
      const profileData = JSON.parse(profileDataString || "{}");
      const loggedInUserId = profileData?.id;

      this.setState(
        { selectedUserId, loggedInUserId },
        () => userSlug && this.handleGetProfilePageData()
      );
    } catch (error) {
      console.error("Error decoding user slug:", error);
      this.handleAccountNotFound();
    }
  };

  handleGetProfilePageData = () => {
    const { selectedUserId, loggedInUserId } = this.state;
    const isOwnProfile = selectedUserId === loggedInUserId;
    
    this.getProfileDetails();
    if (isOwnProfile) {
      this.getCategoryList();
      this.getLanguageDetails();
      this.getLanguageList();
    }
  };

  handleSendMessage = () => {
    const { selectedUserId } = this.state
    if(selectedUserId) {
    const message = new Message(getName(MessageEnum.NavigationMessage));
    message.addData(getName(MessageEnum.NavigationTargetMessage), "Chat");

    message.addData(getName(MessageEnum.NavigationPropsMessage), this.props);

    const raiseMessage: Message = new Message(
      getName(MessageEnum.NavigationPayLoadMessage)
    );
    raiseMessage.addData(getName(MessageEnum.SessionResponseData), {
      userUID: selectedUserId,
    });
    message.addData(getName(MessageEnum.NavigationRaiseMessage), raiseMessage);

    this.send(message);
    }
  }

  handleCopyProfileLink = () => {
    const isCopied = copy(window.location.href)
    if(isCopied) {
      this.openInfoDialog('success', 'Profile link copied!', 1500)
      return
    }
    this.openInfoDialog('error', 'Failed to copy profile link.')
  }

  openPortfolioDialog = (initialIndex: number) => {
    this.scrollPosition = window.scrollY;
    this.setState({
      isPortfolioDialog: true,
      initialPortfolioIndex: initialIndex,
    });
  };

  closePortfolioDialog = () => {
    this.setState({ isPortfolioDialog: false }, () => {
      window.scrollTo({ top: this.scrollPosition, behavior: "auto" });
    });
  };

  getToken = () => {
    const message: Message = new Message(
      getName(MessageEnum.SessionRequestMessage)
    );
    this.send(message);
  };

  onCustomizableProfileData = (data: IProfileDataWeb | null) => {
    if (data === null)
      return this.setState({ currentProfile: { ...this.state.profile } });
    if (!data) return;
    if (!data.attributes || !data.attributes.user_profile_data)
      return this.setState({ currentProfile: { ...this.state.profile } });

    const newProfile = data.attributes.user_profile_data;
    this.setState({ profile: newProfile, currentProfile: { ...newProfile } });
  };

  checkProfileAccount = (message: Message) => {
    const apiRequestCallId = message.getData(
      getName(MessageEnum.RestAPIResponceDataMessage)
    );
    const responseJson = message.getData(
      getName(MessageEnum.RestAPIResponceSuccessMessage)
    );
    if (apiRequestCallId === this.getProfileAccountID) {
      if (!responseJson.data || !responseJson.data.attributes) return;
      const dataResponse = responseJson;
      const userProfile = dataResponse.data.attributes.user_profile_data || {};
      const about_me =
        dataResponse.data.attributes.profile_bio &&
        dataResponse.data.attributes.profile_bio.about_me;
      const qr_code =
        dataResponse.data.attributes.qr_code &&
        dataResponse.data.attributes.qr_code.qr_code;
      this.setState({
        address: dataResponse.data.attributes.address,
        user_name: userProfile.user_name,
        aboutMe: about_me,
        qrCode: qr_code,
        instagram: dataResponse.data.attributes.instagram,
        city: dataResponse.data.attributes.city,
        postalCode: dataResponse.data.attributes.postal_code,
        fullName: dataResponse.data.attributes.name,
        first_name: dataResponse.data.attributes.first_name,
        lastName: dataResponse.data.attributes.last_name,
        profilePicture: userProfile.profile_pic,
        user_type: userProfile.user_type,
        dateOfBirth: dataResponse.data.attributes.dob,
        video: dataResponse.data.attributes.video,
        youtube: dataResponse.data.attributes.youtube,
        facebook: dataResponse.data.attributes.facebook,
        biography: dataResponse.data.attributes.bio,
        attributesValues: Object.keys(
          dataResponse.data.attributes.user_profile_data
        ).map((item) => {
          return {
            ...this.state.attributes[item as unknown as number],
            value: dataResponse.data.attributes.user_profile_data[item],
          };
        }),
      });
      this.setState({
        attributes: this.state.attributesValues.map((item, index) => {
          return {
            ...this.state.attributes[index],
            value: item.value,
          };
        }),
      });
      return true;
    }
  };

  checkOtherResponses = (message: Message) => {
    const apiRequestCallId = message.getData(
      getName(MessageEnum.RestAPIResponceDataMessage)
    );
    const responseJson = message.getData(
      getName(MessageEnum.RestAPIResponceSuccessMessage)
    );

    if (apiRequestCallId === this.getCustomizableProfileCallId) {
      this.onCustomizableProfileData(responseJson.data);
    } else if (apiRequestCallId === this.getCustomizableProfileFieldsCallId) {
      if (!responseJson.data) return;
      const fields = responseJson.data;
      this.setState({ fields });
    } else if (apiRequestCallId === this.updateCustomizableProfileCallId) {
      this.setState({
        currentProfile: { ...this.state.profile },
        saveEnabled: false,
        cancelEnabled: false,
      });
    } else if (apiRequestCallId === this.updateProfileID) {
      this.fetchProfileData();
      alert("Profile Updated Successfully");
    } else if (apiRequestCallId === this.getAttributesID) {
      this.fetchProfileData();
      this.setState({
        attributes: responseJson.data.map((item: object) => {
          return {
            ...item,
            value: "",
          };
        }),
      });
    }
  };

  async receive(from: string, message: Message) {
    // Customizable Area Start
    if (getName(MessageEnum.RestAPIResponceMessage) === message.id) {
      const apiRequestCallId = message.getData(
        getName(MessageEnum.RestAPIResponceDataMessage)
      );
      var responseJson = message.getData(
        getName(MessageEnum.RestAPIResponceSuccessMessage)
      );
      if (apiRequestCallId && responseJson) {
        this.handleApiResponse(apiRequestCallId, responseJson);
      }
    }
    // Customizable Area End
  }

  // Customizable Area Start
  handleApiResponse = (apiRequestCallId: string, responseJson: any) => {
    if (apiRequestCallId === this.deleteCareerHightlightApiCallId) {
      this.removeCareerHighlightFromState(
        this.state.selectedCareerHighlightIndex
      );
      return;
    }
    if (apiRequestCallId === this.deleteEducationApiCallId) {
      this.removeEducationFromState(this.state.selectedEducationIndex);
      return;
    }
    const apiResponseHandlers: Record<string, (responseJson: any) => void> = {
      [this.userProfileUploadApiCallId]:
        this.handleUploadProfileResponse.bind(this),
      [this.userCoverUploadApiCallId]:
        this.handleUploadCoverResponse.bind(this),
      [this.deleteProfilePictureApiCallId]:
        this.handleDeleteProfileResponse.bind(this),
      [this.deleteCoverPictureApiCallId]:
        this.handleDeleteCoverResponse.bind(this),
      [this.userEditSocialLinkApiCallId]:
        this.handleEditSocialResponse.bind(this),
      [this.editUserAboutApiCallId]: this.handleEditUserResponse.bind(this),
      [this.userEditPortfolioLinkApiCallId]:
        this.handleEditPortfolioResponse.bind(this),
      [this.getCategoryLIstApiCallId]: this.handleGetCategoryList.bind(this),
      [this.getSubCategoryListApiCallId]:
        this.handleGetSubCategoryList.bind(this),
      [this.getUserProfileDetailsApiCallId]:
        this.handleGetProfileDetails.bind(this),
      [this.getUserProfileLocationApiCallId]:
        this.handleLocationDetails.bind(this),
      [this.getLanguageListingApiCallId]: this.handleGetLanguageList.bind(this),
      [this.editUserProfileApiCallId]:
        this.handleEditBasicInfoResponse.bind(this),
      [this.editProfessionalSkillApiCallId]:
        this.handleProfessionalSkillsResponse.bind(this),
      [this.deleteSkillsApiCallId]: this.handleDeleteSkillsResponse.bind(this),
      [this.deletePortfolioApiCallId]:
        this.handleUserPortfolioDeleteResponse.bind(this),
      [this.editPortfolioApiCallId]:
        this.handleUserEditPortfolioResponse.bind(this),
    };
    const handler = apiResponseHandlers[apiRequestCallId];
    if (handler) {
      handler(responseJson);
    }
  };

  handleChangeAttributeValue = (value: string, index: number) => {
    const temporaryValue = this.state.attributes;
    temporaryValue[index].value = value;
    this.setState({ attributes: temporaryValue });
  };

  // web events
  setInputValue = (text: string) => {
    this.setState({ txtInputValue: text });
  };

  checkRequiredFields = () => {
    if (!this.state.saveEnabled) {
      for (const field of this.state.fields) {
        if (field.is_required && !this.state.profile[field.name]) return;
      }
      this.setState({ saveEnabled: true });
    } else {
      for (const field of this.state.fields) {
        if (field.is_required && this.state.profile[field.name] === undefined)
          return this.setState({ saveEnabled: false });
      }
    }
  };

  changeFormValue = (
    valueKey: string,
    value: string | Date | number | boolean | null,
    fieldType: FieldType
  ) => {
    if (value !== undefined) {
      if (fieldType === "float") value = parseFloat(value as string);
      else if (fieldType === "integer") value = parseInt(value as string);
      else if (fieldType === "date") value = (value as Date).toDateString();
      else if (fieldType === "boolean") value = value === "true";
      else if (fieldType === "checkbox") value = !!value;
    }
    this.setState({ profile: { ...this.state.profile, [valueKey]: value } });
  };

  onKeyPress = (event: React.KeyboardEvent, field: IField) => {
    if (
      field.field_type === "integer" &&
      (event.key === "." || event.key === ",")
    ) {
      event.preventDefault();
    }
  };

  getCustomizableProfileFields = () => {
    const header = {
      "Content-Type": configJSON.searchApiContentType,
      token: this.token,
    };
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.getCustomizableProfileFieldsCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      `${configJSON.getCustomizableProfileFieldsEndPoint}`
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify(header)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpGetMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };
  getCustomizableProfile = () => {
    const header = {
      "Content-Type": configJSON.searchApiContentType,
      token: this.token,
    };
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.getCustomizableProfileCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      `${configJSON.getCustomizableProfileEndPoint}`
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify(header)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpGetMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };
  checkForRequiredFields = () => {
    const RequiredField = this.state.attributes.map((item: IAttribute) => {
      if (
        item.is_required &&
        !(item.value as string).trim() &&
        item.is_enable
      ) {
        alert(`Please Enter ${item.name}`);
        return false;
      }
      return true;
    });
    if (RequiredField?.includes(false)) {
      return false;
    } else {
      this.updateProfile();
    }
  };

  cancelChanges = () => {
    if (this.state.currentProfile)
      this.setState({
        profile: { ...this.state.currentProfile },
        saveEnabled: false,
        cancelEnabled: false,
      });
  };

  updateCustomizableProfile = () => {
    const header = {
      "Content-Type": configJSON.searchApiContentType,
      token: this.token,
    };

    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.updateCustomizableProfileCallId = requestMessage.messageId;

    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      `${configJSON.updateCustomizableProfileEndPoint}`
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify(header)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestBodyMessage),
      JSON.stringify(this.state.profile)
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpPutMethod
    );

    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  fetchProfileData = async () => {
    const header = {
      "Content-Type": "application/json",
      token: configJSON.temporaryToken,
    };

    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );

    this.getProfileAccountID = requestMessage.messageId;

    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.baseURLGetProfileAccount
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify(header)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.validationApiMethodType
    );

    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  fetchAttributes = async () => {
    const header = {
      "Content-Type": "application/json",
      token: configJSON.temporaryToken,
    };

    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );

    this.getAttributesID = requestMessage.messageId;

    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.getFields
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify(header)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.validationApiMethodType
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  updateProfile = async () => {
    const header = {
      "Content-Type": "application/json",
      token: configJSON.temporaryToken,
    };

    const deliveredData = this.state.attributes
      .map((item) => {
        if (item && item.is_enable) {
          return {
            [item.name]: item.value,
          };
        }
      })
      .filter((item) => item != undefined);

    const filteredPosts: IProfileData = {};

    deliveredData.map((item) => {
      filteredPosts[Object.keys(item as object)[0]] = Object.values(
        item as object
      )[0];
    });

    const dataToBeUsed = {
      profile: filteredPosts,
    };

    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );

    this.updateProfileID = requestMessage.messageId;

    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.updateProfileURL
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify(header)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.putMethod
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestBodyMessage),
      JSON.stringify(dataToBeUsed)
    );

    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  modalVisible = (value: boolean) => {
    this.setState({ isProfileModalOpen: value });
  };

  handleCoverPhoto = (value: boolean) => {
    this.setState({ isCoverPhotoModalOpen: value });
  };

  handleEditAbout = (value: boolean) => {
    this.setState({
      isEditAboutOpen: value,
      editedAbout: this.state.aboutDetails || "",
    });
  };

  validationPortfolioLinkSchema = Yup.object().shape({
    portfolioLink1: Yup.string().trim().url("Enter a valid URL"),
    portfolioLink2: Yup.string().trim().url("Enter a valid URL"),
    portfolioLink3: Yup.string().trim().url("Enter a valid URL"),
    portfolioLink4: Yup.string().trim().url("Enter a valid URL"),
    portfolioLink5: Yup.string().trim().url("Enter a valid URL"),
  });

  handleDeleteEducation = (id: string, index: number) => {
    this.setState({
      isDeleteEducationModal: true,
      selectedEducationId: id,
      selectedEducationIndex: index,
    });
  };

  handleConfirmDeleteEducation = () => {
    const { selectedEducationId, selectedEducationIndex } = this.state;
    this.setState({ isDeleteEducationModal: false }, () => {
      this.deleteEducation(selectedEducationId, selectedEducationIndex);
    });
  };

  handleAboutChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ editedAbout: event.target.value });
  };

  handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "profile" | "cover"
  ) => {
    const file = event.target.files?.[0] || null;

    if (!file) return;

    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    const validFileTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (file.size > MAX_FILE_SIZE) {
      if (type === "profile") {
        this.setState({
          image: null,
          selectedProfileImagePreview: "",
          selectedCoverImagePreview: this.state.selectedCoverImagePreview,
          imageError: "File is too large. Max size is 10MB",
        });
      } else {
        this.setState({
          image: null,
          selectedCoverImagePreview: "",
          selectedProfileImagePreview: this.state.selectedProfileImagePreview,
          imageError: "File is too large. Max size is 10MB",
        });
      }
      return;
    }
    if (!validFileTypes.includes(file.type)) {
      if (type === "profile") {
        this.setState({
          image: null,
          selectedProfileImagePreview: "",
          selectedCoverImagePreview: this.state.selectedCoverImagePreview,
          imageError: "Only JPEG, JPG, and PNG files are allowed",
        });
      } else {
        this.setState({
          image: null,
          selectedCoverImagePreview: "",
          selectedProfileImagePreview: this.state.selectedProfileImagePreview,
          imageError: "Only JPEG, JPG, and PNG files are allowed",
        });
      }
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === "profile") {
        this.setState({
          image: file,
          selectedProfileImagePreview: reader.result as string,
          selectedCoverImagePreview: this.state.selectedCoverImagePreview,
          imageError: "",
        });
      } else {
        this.setState({
          image: file,
          selectedCoverImagePreview: reader.result as string,
          selectedProfileImagePreview: this.state.selectedProfileImagePreview,
          imageError: "",
        });
      }
    };
    reader.readAsDataURL(file);
  };

  handleProfiledelete = () => {
    setTimeout(() => {
      this.setState({ isProfileModalOpen: false, profileImgPreview: null });
    }, 1000);
  };

  handleCoverFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const imgFile = event.target.files?.[0] || null;
    if (imgFile) {
      const MAX_IMG_SIZE = 10 * 1024 * 1024;
      if (imgFile.size > MAX_IMG_SIZE) {
        this.setState({
          imageError: "File is too large. Max size is 10MB",
        });
        return;
      }
      const validFileTypes = ["image/jpg", "image/jpeg", "image/png"];
      if (!validFileTypes.includes(imgFile.type)) {
        this.setState({
          imageError: "Only JPG , JPEG and PNG files are allowed.",
        });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        this.setState({
          image: imgFile,
          coverImgPreview: reader.result as string,
        });
      };
      reader.readAsDataURL(imgFile);
    }
    this.setState({ isCoverPhotoModalOpen: false });
  };

  handleCoverdelete = () => {
    setTimeout(() => {
      this.setState({ isCoverPhotoModalOpen: false, coverImgPreview: null });
    }, 1000);
  };

  handleCloseCareerModal = () => {
    this.setState({ isCareerModalOpen: false });
  };

  handleCloseEducationModal = () => {
    this.setState({ isEducationModalOpen: false });
  };

  handleClosePortfolioModal = () => {
    this.setState({ isPortfolioModalOpen: false });
  };

  handleCloseSocialModal = () => {
    this.setState({ isSocialMediaModalOpen: false });
  };

  handleSocialModal = () => {
    this.setState({ isSocialMediaModalOpen: true });
  };

  handlePortfolioModal = () => {
    this.setState({ isPortfolioModalOpen: true });
  };

  handleMediaClick = (mediaUrl: string | null) => {
    if (!mediaUrl) return;
    this.setState({
      expandedMedia: mediaUrl,
      isMediaModalOpen: true,
    });
  };

  handleCloseMediaDialog = () => {
    this.setState({
      expandedMedia: "",
      isMediaModalOpen: false,
    });
  };

  handleSaveCareerHighlights = () => {
    this.setState(
      {
        selectedCareerHighlight: null,
        isEditingCareerHighlight: false,
        isCareerModalOpen: false,
      },
      () => {
        this.getProfileDetails();
      }
    );
  };

  handleEditCareer = (highlight: CareerHighlight | null = null) => {
    if (highlight) {
      this.setState({
        selectedCareerHighlight: highlight,
        isEditingCareerHighlight: true,
        isCareerModalOpen: true,
      });
    } else {
      this.setState({
        selectedCareerHighlight: null,
        isEditingCareerHighlight: false,
        isCareerModalOpen: true,
      });
    }
  };

  handleEducationModal = (education: Education | null = null) => {
    if (education) {
      this.setState({
        selectedEducation: education,
        isEditingEducation: true,
        isEducationModalOpen: true,
      });
    } else {
      this.setState({
        selectedEducation: null,
        isEditingEducation: false,
        isEducationModalOpen: true,
      });
    }
  };

  handleSaveEducationDetails = () => {
    this.setState(
      {
        selectedEducation: null,
        isEditingEducation: false,
        isEducationModalOpen: false,
      },
      () => {
        this.getProfileDetails();
      }
    );
  };

  validationInfoSchema = Yup.object().shape({
    firstName: Yup.string()
      .trim()
      .required("First Name is required")
      .matches(/^[A-Za-z]+$/, "Only alphabets are allowed!")
      .max(30, "First name must be at most 30 characters!"),
    lastName: Yup.string()
      .trim()
      .required("Last Name is required")
      .matches(/^[A-Za-z]+$/, "Only alphabets are allowed!")
      .max(30, "Last name must be at most 30 characters!"),
    location: Yup.string().trim().required("Location is required"),
    selectedLanguages: Yup.array().min(
      1,
      "At least one language must be selected"
    ),
    height: Yup.string()
      .matches(
        /^[0-9]+(\.[0-9]+)?$/,
        "Height must be a valid number (only digits and one dot allowed)."
      )
      .required("Height is required"),
    weight: Yup.string()
      .matches(
        /^[0-9]+(\.[0-9]+)?$/,
        "Weight must be a valid number (only digits and one dot allowed)."
      )
      .required("Weight is required"),
    age: Yup.number()
      .typeError("Age must be a number")
      .positive("Age must be greater than zero")
      .integer("Age must be a whole number")
      .required("Age is required"),
    userGender: Yup.string().trim().required("Gender is required")
  });

  handleBasicModal = () => {
    this.setState({ isBasicInfoModalOpen: true });
  };

  handleSubmitBasicInfo = () => {
    const { tempProfileData } = this.state;
    this.validationInfoSchema
      .validate(tempProfileData, { abortEarly: false, stripUnknown: true })
      .then(() => {
        this.setState({ errors: {} }, () => {
          this.editBasicInfo();
        });
      })
      .catch((err) => {
        const errorMessages: { [key: string]: string } = {};
        err.inner.forEach(
          (error: { path: string | number; message: string }) => {
            if (error.path) errorMessages[error.path] = error.message;
          }
        );
        this.setState({ errors: errorMessages });
      });
  };

  handleChange = (event: ChangeEvent<HTMLInputElement> | SelectChangeEvent) => {
    const { name, value } = event.target;
    this.setState((prevState) => ({
      tempProfileData: {
        ...prevState.tempProfileData,
        [name]: value,
      },
    }));
  };

  handleGenderChange = (
    event: ChangeEvent<HTMLInputElement> | SelectChangeEvent
  ) => {
    this.setState((prevState) => ({
      tempProfileData: {
        ...prevState.tempProfileData,
        userGender: event.target.value,
      },
    }));
  };

  handleLocationChange = (
    event: ChangeEvent<HTMLInputElement> | SelectChangeEvent
  ) => {
    this.setState((prevState) => ({
      tempProfileData: {
        ...prevState.tempProfileData,
        location: event.target.value,
      },
    }));
  };

  handleCloseBasicModal = () => {
    this.setState({
      isBasicInfoModalOpen: false,
    });
  };

  CustomUncheckedIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="0.5" y="0.5" width="19" height="19" rx="5.5" stroke="#94A3B8" />
    </svg>
  );

  CustomCheckedIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="20" height="20" rx="6" fill="#652784" />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M15.0611 5.42238C15.5183 5.73229 15.6376 6.35411 15.3277 6.81124L10.2432 14.3112C10.0771 14.5562 9.8111 14.715 9.51671 14.745C9.22232 14.7749 8.92977 14.673 8.71777 14.4665L4.80234 10.6536C4.40667 10.2683 4.39827 9.6352 4.78358 9.23953C5.16888 8.84386 5.80199 8.83546 6.19766 9.22077L9.25771 12.2007L13.6723 5.68895C13.9822 5.23182 14.604 5.11247 15.0611 5.42238Z"
        fill="white"
      />
    </svg>
  );

  handleDeleteLanguage = (language: string) => {
    this.setState((prevState) => ({
      tempProfileData: {
        ...prevState.tempProfileData,
        selectedLanguages: prevState.tempProfileData.selectedLanguages.filter(
          (lang: string) => lang !== language
        ),
      },
    }));
  };

  handleLanguageChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    this.setState((prevState) => {
      const selectedLanguages =
        typeof value === "string" ? value.split(",") : value;
      const uniqueLanguages = Array.from(new Set(selectedLanguages));
      return {
        tempProfileData: {
          ...prevState.tempProfileData,
          selectedLanguages: uniqueLanguages,
        },
      };
    });
  };

  handleUploadedFiles = (uploadedFiles: PortfolioItem[]) => {
    this.setState({ portfolioImgs: uploadedFiles });
    this.getProfileDetails();
  };

  handleEditPortfolioModalClose = () => {
    this.setState({ iseditPortfolioImgOpen: false, validPortfolioError: [] });
  };

  handleEditImage = (event: React.MouseEvent, id: number) => {
    event.stopPropagation();
    this.setState({
      iseditPortfolioImgOpen: true,
      selectedPortfolioImageIndex: id,
      selectedPortfolioImage: this.state.portfolioImgs[id],
    });
  };

  determineMediaType = (type: string): FileTypeCategory => {
    const FILE_CATEGORIES: Record<string, FileTypeCategory> = {
      image: "image",
      audio: "audio",
      video: "video",
      pdf: "application/pdf",
    };
    return FILE_CATEGORIES[type?.split("/")[0]] || "application/pdf";
  };

  validateFiles = (
    files: File[] | FileList,
    replacingFileIndex: number | null = null,
    replacingFileType: FileTypeCategory | null = null
  ) => {
    const validMediaFiles: File[] = [];
    const errors: string[] = [];
    const mediaFileCounts: Record<FileTypeCategory, number> = {
      image: 0,
      audio: 0,
      video: 0,
      "application/pdf": 0,
    };
    this.state.portfolioImgs.forEach(
      ({ type }: { type: string }, index: number) => {
        const mediaType = this.determineMediaType(type);
        if (replacingFileIndex !== null && index === replacingFileIndex) return;
        mediaFileCounts[mediaType]++;
      }
    );

    const validateMedia = (file: File): string | null => {
      const type = this.determineMediaType(file.type);
      if (replacingFileType !== null && replacingFileType !== type) {
        mediaFileCounts[replacingFileType]--;
      }
      if (mediaFileCounts[type] >= MAX_LIMITS[type]) {
        return `You can upload a maximum of ${MAX_LIMITS[type]} ${type}s.`;
      }
      const mediaValidation = [
        {
          condition: !ALLOWED_MEDIA_TYPE.includes(file.type),
          message: `${file.name} has an invalid file type.`,
        },
        {
          condition: file.size > MAX_MEDIA_SIZES[type],
          message: `${file.name} exceeds the ${
            MAX_MEDIA_SIZES[type] / (1024 * 1024)
          } MB limit.`,
        },
      ];
      return (
        mediaValidation.find(({ condition }) => condition)?.message || null
      );
    };
    Array.from(files).forEach((file) => {
      const error = validateMedia(file);
      if (error) {
        errors.push(error);
      } else {
        const fileType = this.determineMediaType(file.type);
        mediaFileCounts[fileType]++;
        validMediaFiles.push(file);
      }
    });

    this.setState({
      validPortfolioError: errors.length > 0 ? errors : [],
    });
    return { isValid: errors.length === 0, validFiles: validMediaFiles };
  };

  handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const replacingIndex = this.state.selectedPortfolioImageIndex;
    const isReplacing = replacingIndex !== null && replacingIndex >= 0;

    let replacingFileType: PortfolioItem["type"] | null = null;
    let replacingItemId: number | null = null;
    if (isReplacing && replacingIndex !== null) {
      replacingFileType = this.determineMediaType(
        this.state.portfolioImgs[replacingIndex].type
      );
      replacingItemId = this.state.portfolioImgs[replacingIndex].id;
    }

    const validationResult = this.validateFiles(
      [file] as unknown as FileList,
      isReplacing ? replacingIndex : null,
      replacingFileType
    );
    if (!validationResult.isValid) return;
    const reader = new FileReader();
    reader.onloadend = async () => {
      if (isReplacing && replacingIndex !== null && replacingItemId !== null) {
        await this.editUserPortfolio(file, replacingItemId);
      }
    };
    reader.readAsDataURL(file);
  };

  handleProfessionalModal = (value: boolean) => {
    this.setState({ isProfessionalModalOpen: value });
  };

  handleCloseProfessionalModal = () => {
    this.setState({
      isProfessionalModalOpen: false,
    });
  };

  handleCategoryChange = (event: SelectChangeEvent<string>) => {
    const selectedCategory = event.target.value;
    this.setState({
      selectedCategory,
      selectedSubCategory: [],
      subCategories: [],
    });
    this.getSubCategoryList(selectedCategory);
  };

  handleSubCategoryChange = (event: SelectChangeEvent<string[]>) => {
    this.setState({ selectedSubCategory: event.target.value as string[] });
  };

  handleDropdownClose = () => {
    const { selectedCategory, selectedSubCategory, selectedSkills } =
      this.state;

    if (selectedCategory && selectedSubCategory.length > 0) {
      const newSkills = selectedSubCategory.map(
        (sub: string | any): Skill => ({
          category: selectedCategory,
          subCategory: sub.name || sub,
          level: "Beginner" as "Beginner",
          subCategoryId: sub?.id ?? 0,
        })
      );

      this.setState({
        selectedSkills: [
          ...selectedSkills,
          ...newSkills.filter(
            (newSkill) =>
              !selectedSkills.some(
                (skill) => skill.subCategory === newSkill.subCategory
              )
          ),
        ],
        selectedSubCategory: [],
      });
    }
  };

  handleSkillLevelChange = (
    index: number,
    level: "Beginner" | "Intermediate" | "Expert"
  ) => {
    const updatedSkills = [...this.state.selectedSkills];
    updatedSkills[index].level = level;
    this.setState({ selectedSkills: updatedSkills });
  };

  handleDeleteSkill = (subCategory: string) => {
    const skillIndex = this.state.selectedSkills.findIndex(
      (s) => s.subCategory === subCategory
    );
    if (skillIndex !== -1) {
      const subCategoryId = this.state.selectedSkills[skillIndex].subCategoryId;
      this.setState({
        skillToDeleteIndex: skillIndex,
        skillToDeleteId: subCategoryId,
        isDeleteSkillModal: true,
      });
    }
  };

  convertToRoman = (num: number) => {
    const roman = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
    return roman[num - 1] || num;
  };

  handleSaveProfessionalSkills = async () => {
    await this.editProfessionalSkills();
    this.setState({
      savedSkills: this.state.selectedSkills,
      isProfessionalModalOpen: false,
    });
    this.getProfileDetails();
  };

  getDotCount = (level: string) => {
    switch (level) {
      case "beginner":
        return 1;
      case "intermediate":
        return 2;
      case "expert":
        return 3;
      default:
        return 0;
    }
  };

  handleCloseProfileModal = () => {
    this.setState({
      isProfileModalOpen: false,
      image: null,
      imageError: "",
      selectedProfileImagePreview: "",
    });
  };

  handleCloseProfileCoverModal = () => {
    this.setState({
      image: null,
      imageError: "",
      isCoverPhotoModalOpen: false,
      selectedCoverImagePreview: "",
    });
  };

  userProfileUpload = async () => {
    this.setState({ isLoading: true });
    const token = await getStorageData(configJSON.TOKEN);
    const formData = new FormData();
    if (!this.state.image) {
      this.setState({ imageError: "No image selected." });
      return;
    }
    formData.append("user_image", this.state.image);
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.userProfileUploadApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.updateProfilePicture
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestBodyMessage),
      formData
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpPutMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleUploadProfileResponse = async (
    responseJson: ICreateProfileSuccessResponse | ICreateProfileErrorResponse
  ) => {
    if ("data" in responseJson) {
      const {
        id,
        attributes: {
          first_name,
          last_name,
          email,
          full_phone_number,
          user_image_url,
        },
      } = responseJson.data;
      const userProfile = {
        id,
        firstName: first_name,
        lastName: last_name,
        email,
        phoneNumber: full_phone_number,
        profileImg: user_image_url,
        lastFetched: Date.now(),
      };
      await setStorageData(configJSON.PROFILE, JSON.stringify(userProfile));
      this.setState({
        profileImgPreview: user_image_url,
        isProfileModalOpen: false,
        selectedProfileImagePreview: "",
        image: null,
        imageError: "",
        isLoading: false,
      });
    } else {
      this.setState({
        isLoading: false,
        imageError: JSON.stringify(responseJson.errors),
      });
    }
  };

  userCoverUpload = async () => {
    this.setState({ isLoading: true });
    const token = await getStorageData(configJSON.TOKEN);
    const formData = new FormData();
    if (!this.state.image) {
      this.setState({ imageError: "No image selected." });
      return;
    }
    formData.append("cover_photo", this.state.image);
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.userCoverUploadApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.updateCoverPicture
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestBodyMessage),
      formData
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpPutMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleUploadCoverResponse = (
    responseJson: ICreateProfileSuccessResponse | ICreateProfileErrorResponse
  ) => {
    if ("data" in responseJson) {
      this.setState({
        coverImgPreview: responseJson.data.attributes.cover_photo,
        isCoverPhotoModalOpen: false,
        selectedCoverImagePreview: "",
        image: null,
        imageError: "",
        isLoading: false,
      });
    } else {
      this.setState({
        isLoading: false,
        imageError: JSON.stringify(responseJson.errors),
      });
    }
  };

  userProfileDelete = async () => {
    const token = await getStorageData(configJSON.TOKEN);
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.deleteProfilePictureApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.deleteProfilePicture
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpDeleteMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleDeleteProfileResponse = (
    responseJson: IDeleteProfileSuccessResponse | ICreateProfileErrorResponse
  ) => {
    if (responseJson) {
      this.setState({ isProfileModalOpen: false, profileImgPreview: null });
      this.getProfileDetails();
    } else {
      this.setState({
        imageError: JSON.stringify(responseJson),
      });
    }
  };

  userCoverDelete = async () => {
    const token = await getStorageData(configJSON.TOKEN);
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.deleteCoverPictureApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.deleteCoverPicture
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpDeleteMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleDeleteCoverResponse = (
    responseJson: IDeleteProfileSuccessResponse | ICreateProfileErrorResponse
  ) => {
    if (responseJson) {
      this.setState({ isCoverPhotoModalOpen: false, coverImgPreview: null });
      this.getProfileDetails();
    } else {
      this.setState({
        imageError: JSON.stringify(responseJson),
      });
    }
  };

  userEditSocial = async (values: SocialMediaLinks) => {
    this.setState({ isLoading: true });
    const token = await getStorageData(configJSON.TOKEN);
    const body = {
      social_media_links: {
        facebook: values.facebookLink,
        instagram: values.instagramLink,
        X: values.X,
        youtube: values.youtubeLink,
        linkedin: values.linkedinLink,
      },
    };
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.userEditSocialLinkApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.editSocialLinks
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ "Content-Type": "application/json", token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestBodyMessage),
      JSON.stringify(body)
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpPutMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  editUserProfile = async () => {
    this.setState({ isLoading: true });
    const token = await getStorageData(configJSON.TOKEN);
    const body = { description: this.state.editedAbout };
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.editUserAboutApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.editProfileDetails
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ "Content-Type": "application/json", token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestBodyMessage),
      JSON.stringify(body)
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpPutMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleEditSocialResponse = (
    responseJson: ICreateProfileSuccessResponse | ICreateProfileErrorResponse
  ) => {
    if ("data" in responseJson) {
      const updatedLinks = responseJson.data.attributes.social_media_links;
      this.setState({
        isSocialMediaModalOpen: false,
        socialLinks: {
          facebookLink: updatedLinks.facebook || "",
          instagramLink: updatedLinks.instagram || "",
          X: updatedLinks.X || "",
          youtubeLink: updatedLinks.youtube || "",
          linkedinLink: updatedLinks.linkedin || "",
        },
      });
    }
    this.setState({ isLoading: false });
  };

  handleEditUserResponse = (
    responseJson: ICreateProfileSuccessResponse | ICreateProfileErrorResponse
  ) => {
    if (responseJson) {
      this.setState({
        aboutDetails: this.state.editedAbout,
        isEditAboutOpen: false,
      });
    }
    this.setState({ isLoading: false });
  };

  userEditPortfolioLink = async (values: PortfolioLinks) => {
    this.setState({ isLoading: true });
    const token = await getStorageData(configJSON.TOKEN);
    const body = {
      account: {
        user_links: {
          "Portfolio Link 1": values.portfolioLink1,
          "Portfolio Link 2": values.portfolioLink2,
          "Portfolio Link 3": values.portfolioLink3,
          "Portfolio Link 4": values.portfolioLink4,
          "Portfolio Link 5": values.portfolioLink5,
        },
      },
    };
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.userEditPortfolioLinkApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.editPortfolioLink
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ "Content-Type": "application/json", token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestBodyMessage),
      JSON.stringify(body)
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpPutMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleEditPortfolioResponse = (
    responseJson: ICreateProfileSuccessResponse | ICreateProfileErrorResponse
  ) => {
    if ("data" in responseJson) {
      const userLinks = responseJson.data.attributes.user_links;
      this.setState({
        portfolioLinks: {
          portfolioLink1: userLinks["Portfolio Link 1"] || "",
          portfolioLink2: userLinks["Portfolio Link 2"] || "",
          portfolioLink3: userLinks["Portfolio Link 3"] || "",
          portfolioLink4: userLinks["Portfolio Link 4"] || "",
          portfolioLink5: userLinks["Portfolio Link 5"] || "",
        },
        isPortfolioModalOpen: false,
      });
    }
    this.setState({ isLoading: false });
  };

  getCategoryList = async () => {
    const token = await getStorageData(configJSON.TOKEN);
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.getCategoryLIstApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.getCategoryListing
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ "Content-Type": "application/json", token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpGetMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleGetCategoryList = (responseJson: CategoryResponse) => {
    if (Array.isArray(responseJson?.data)) {
      const categoriesList: Category[] = responseJson.data.map((item) => ({
        id: item.attributes.id,
        name: item.attributes.name,
        sub_categories: [],
      }));
      this.setState({ categoriesList });
    }
  };

  getSubCategoryList = async (categoryName: string) => {
    const token = await getStorageData(configJSON.TOKEN);
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.getSubCategoryListApiCallId = requestMessage.messageId;
    const subCategoryUrl = `${
      configJSON.getCategoryListing
    }?category_name=${encodeURIComponent(categoryName)}`;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      subCategoryUrl
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ "Content-Type": "application/json", token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpGetMethod
    );

    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleGetSubCategoryList = (responseJson: SubCategoryResponse) => {
    if (Array.isArray(responseJson?.data)) {
      const subCategories: string[] = responseJson.data.map(
        (item) => item.attributes.name
      );
      this.setState({ subCategories });
    }
  };

  getProfileDetails = async () => {
    this.setState({ isLoading: true });
    const { selectedUserId, loggedInUserId } = this.state;
    const token = await getStorageData(configJSON.TOKEN);
    const userId = selectedUserId || loggedInUserId;
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.getUserProfileDetailsApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      `${configJSON.getProfileDetails}/${userId}`
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ "Content-Type": "application/json", token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpGetMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getLanguageDetails = async () => {
    const header = {
      "Content-Type": configJSON.validationApiContentType,
    };

    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );

    this.getUserProfileLocationApiCallId = requestMessage.messageId;

    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.getLocationDetails
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify(header)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpGetMethod
    );
    
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleLocationDetails = (responseJson: LocationOption) => {
    if (Array.isArray(responseJson)) {
      const transformedLocations: LocationOption[] = responseJson.map(
        (loc: string, index: number) => ({
          key: `${index}-${loc}`,
          value: loc,
        })
      );
      this.setState({ locationOptions: transformedLocations });
    }
  };

  handleAccountNotFound = (message?: string) => {
    this.infoDialogCallback = () => this.props.navigation.goBack();
    this.openInfoDialog("error", message || "Account not found!");
  };

  openInfoDialog = (infoType: InfoType, infoMessage: string = "", autoCloseTime?: number) => {
    this.setState({
      isInfoDialog: true,
      infoType,
      infoMessage,
      isLoading: false,
    });
    if(autoCloseTime) {
      setTimeout(() => this.closeInfoDialog(), autoCloseTime)
    }
  };

  closeInfoDialog = () => {
    this.setState(
      {
        isInfoDialog: false,
      },
      () => {
        if (this.infoDialogCallback) {
          this.infoDialogCallback();
          this.infoDialogCallback = null;
        }
      }
    );
  };

  capitalize = (text: string) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "";

  handleGetProfileDetails = (
    responseJson: ICreateProfileSuccessResponse | { error: string }
  ) => {
    if ("data" in responseJson) {
      const response = responseJson?.data?.attributes;
      const userCareerHighlights = response.user_careers || [];
      const socialMediaLinks = response.social_media_links || {};
      const userLinks = response.user_links || {};
      const userImageUrl = response.user_image_url || "";
      const userCoverImageUrl = response.cover_photo || "";
      const portfolioLinks = {
        portfolioLink1: userLinks["Portfolio Link 1"] || "",
        portfolioLink2: userLinks["Portfolio Link 2"] || "",
        portfolioLink3: userLinks["Portfolio Link 3"] || "",
        portfolioLink4: userLinks["Portfolio Link 4"] || "",
        portfolioLink5: userLinks["Portfolio Link 5"] || "",
      };
      const transformedHighlights = userCareerHighlights.map((item: any) => ({
        id: item.id,
        attributes: {
          project_name: item.project_name,
          role: item.role,
          start_date: item.start_date,
          start_year: item.start_year,
          end_date: item.end_date,
          end_year: item.end_year,
          ongoing: item.is_ongoing,
          duration: item.duration,
          location: item.location,
          description: item.description,
          project_link: item.project_link,
          career_images_urls: item.career_images_urls,
        },
      }));
      const aboutUser = response.description;
      const userEducation = response.user_educations || [];
      const transformeduserEducation = userEducation.map((item: any) => ({
        id: item.id,
        attributes: {
          institute_name: item.institute_name,
          qualification: item.qualification,
          start_date: item.start_date,
          end_date: item.end_date,
          start_year: item.start_year,
          end_year: item.end_year,
          is_ongoing: item.is_ongoing,
          location: item.location,
        },
      }));

      type PortfolioType = "video" | "image" | "audio" | "application/pdf";

      const getMediaType = (contentType: string): PortfolioType => {
        if (contentType.startsWith("image")) return "image";
        if (contentType.startsWith("video")) return "video";
        if (contentType.startsWith("audio")) return "audio";
        if (contentType.startsWith("application/pdf")) return "application/pdf";
        return "image";
      };

      const portfolioMedia = (response.upload_media || []).map((item: any) => ({
        id: item.id,
        url: item.url,
        filename: item.filename,
        type: getMediaType(item.content_type),
        contentType: item.content_type,
      }));

      this.setState(
        {
          profileImgPreview: userImageUrl,
          coverImgPreview: userCoverImageUrl,
          careerHighlights: transformedHighlights,
          portfolioLinks,
          educationDetails: transformeduserEducation,
          aboutDetails: aboutUser,
          socialLinks: {
            facebookLink: socialMediaLinks.facebook || "",
            instagramLink: socialMediaLinks.instagram || "",
            X: socialMediaLinks.X || "",
            youtubeLink: socialMediaLinks.youtube || "",
            linkedinLink: socialMediaLinks.linkedin || "",
          },
          profileData: {
            firstName: response.first_name,
            lastName: response.last_name,
            location: response.locations,
            height: response.height || 0,
            weight: response.weight || 0,
            age: response.age,
            userGender: response.gender,
            role: response.roles,
            language: response.languages,
            selectedLanguages: response.languages,
          },
          portfolioImgs: portfolioMedia,
          isLoading: false,
        },
        () => {
          this.setState({
            tempProfileData: { ...this.state.profileData },
          });
        }
      );
      const categories = response.categories || [];
      const apiSelectedSkills = categories.flatMap((category: any) =>
        category.sub_categories.map((sub: any, index: number) => ({
          id: index + 1,
          category: category.name,
          subCategory: sub.name,
          subCategoryId: sub.id,
          level: this.capitalize(sub.experience_level),
        }))
      );
      this.setState({
        categories,
        selectedSkills: [...apiSelectedSkills],
      });
    } else {
      this.infoDialogCallback = () => this.props.navigation.goBack();
      this.openInfoDialog(
        "error",
        responseJson?.error || "Something went wrong!"
      );
    }
  };

  handleDeleteCareerHighlightModal = (id: string, index: number) => {
    this.setState({
      isDeleteCareerHighlightModal: true,
      selectedCareerHighlightId: id,
      selectedCareerHighlightIndex: index,
    });
  };

  handleConfirmDeleteCareerHighlight = () => {
    const { selectedCareerHighlightId, selectedCareerHighlightIndex } =
      this.state;
    this.setState({ isDeleteCareerHighlightModal: false }, () => {
      this.deleteCareerHighlight(
        selectedCareerHighlightId,
        selectedCareerHighlightIndex
      );
    });
  };

  deleteCareerHighlight = async (id: string, index: number) => {
    const token = await getStorageData(configJSON.TOKEN);
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.deleteCareerHightlightApiCallId = requestMessage.messageId;

    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      `${configJSON.addCareerHighlights}/${id}`
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ "Content-Type": "application/json", token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpDeleteMethod
    );

    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  removeCareerHighlightFromState = (index: number) => {
    const updatedCareerHighlights = [...this.state.careerHighlights];
    updatedCareerHighlights.splice(index, 1);
    this.setState({ careerHighlights: updatedCareerHighlights });
    this.getProfileDetails();
  };

  getLanguageList = async () => {
    const header = {
      "Content-Type": configJSON.validationApiContentType,
    };

    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );

    this.getLanguageListingApiCallId = requestMessage.messageId;

    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.getLanguageList
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify(header)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpGetMethod
    );

    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleGetLanguageList = (responseJson: Language[]) => {
    if (Array.isArray(responseJson)) {
      const transformedLanguages: LanguageOption[] = responseJson.map(
        (lang) => ({
          key: String(lang.id),
          value: lang.name,
        })
      );
      this.setState({ languageOptions: transformedLanguages });
    }
  };

  editBasicInfo = async () => {
    const token = await getStorageData(configJSON.TOKEN);
    const { tempProfileData } = this.state;
    const body = {
      first_name: tempProfileData.firstName,
      last_name: tempProfileData.lastName,
      locations: tempProfileData.location,
      languages: tempProfileData.selectedLanguages,
      height: tempProfileData.height,
      weight: tempProfileData.weight,
      age: tempProfileData.age,
      gender: tempProfileData.userGender,
    };
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.editUserProfileApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.editProfileDetails
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ "Content-Type": "application/json", token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestBodyMessage),
      JSON.stringify(body)
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpPutMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleEditBasicInfoResponse = async (
    responseJson: ICreateProfileSuccessResponse | ICreateProfileErrorResponse
  ) => {
    if ("data" in responseJson && "attributes" in responseJson.data) {
      const {
        id,
        attributes: { first_name, last_name, email, full_phone_number },
      } = responseJson.data;
      const userProfile = {
        id,
        firstName: first_name,
        lastName: last_name,
        email,
        phoneNumber: full_phone_number,
        lastFetched: Date.now(),
      };
      await setStorageData(configJSON.PROFILE, JSON.stringify(userProfile));
      this.setState({
        profileData: { ...this.state.tempProfileData },
        isBasicInfoModalOpen: false,
        isEditAboutOpen: false,
        errors: {},
      });
    }
  };

  editProfessionalSkills = async () => {
    const token = await getStorageData(configJSON.TOKEN);
    const groupedSkills: Record<
      string,
      { name: string; experience_level: string }[]
    > = {};

    this.state.selectedSkills.forEach((skill) => {
      if (!groupedSkills[skill.category]) {
        groupedSkills[skill.category] = [];
      }

      groupedSkills[skill.category].push({
        name: skill.subCategory,
        experience_level: skill.level.toLowerCase(),
      });
    });

    const body = {
      categories: Object.entries(groupedSkills).map(
        ([categoryName, sub_categories]) => ({
          name: categoryName,
          sub_categories,
        })
      ),
    };
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.editProfessionalSkillApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.editProfessionalSkill
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ "Content-Type": "application/json", token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestBodyMessage),
      JSON.stringify(body)
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpPutMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleProfessionalSkillsResponse = (
    responseJson: ICreateProfileSuccessResponse | ICreateProfileErrorResponse
  ) => {
    this.getProfileDetails();
    if ("data" in responseJson) {
      const categoriesData = responseJson.data.attributes.categories;
      this.setState({
        categories: categoriesData,
        selectedSkills: [],
      });
      this.getProfileDetails();
    } else {
      this.setState({
        selectedSkills: [],
      });
    }
  };

  userDeleteProfessionalSkills = async () => {
    const token = await getStorageData(configJSON.TOKEN);
    const { skillToDeleteId } = this.state;
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.deleteSkillsApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      `${configJSON.deleteProfessionalSkill}?sub_category_id=${skillToDeleteId}`
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpDeleteMethod
    );

    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleDeleteSkillsResponse = (
    responseJson: IDeleteSkills | IDeleteSkillsError
  ) => {
    if (responseJson) {
      this.setState({
        selectedSkills: [],
        isDeleteSkillModal: false,
        skillToDeleteIndex: null,
        skillToDeleteId: null,
      });
    } else {
      this.setState({
        selectedSkills: [],
      });
    }
    this.getProfileDetails();
  };

  deleteEducation = async (id: string, index: number) => {
    const token = await getStorageData(configJSON.TOKEN);
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.deleteEducationApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      `${configJSON.addEducationAndTraining}/${id}`
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ "Content-Type": "application/json", token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpDeleteMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  removeEducationFromState = (index: number) => {
    const updatedEducation = [...this.state.educationDetails];
    updatedEducation.splice(index, 1);
    this.setState({ educationDetails: updatedEducation });
    this.getProfileDetails();
  };

  handleDeleteImage = (event: React.MouseEvent, id: number) => {
    event.stopPropagation();
    this.setState({
      isdeletePortfolioImgOpen: true,
      deleteItemIndex: id,
    });
  };

  handleConfirmDelete = () => {
    const { portfolioImgs, deleteItemIndex } = this.state;
    if (deleteItemIndex === null || !portfolioImgs[deleteItemIndex]) return;
    const itemId = portfolioImgs[deleteItemIndex].id;
    this.userPortfolioDelete(itemId);
  };

  userPortfolioDelete = async (id: number) => {
    const token = await getStorageData(configJSON.TOKEN);
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.deletePortfolioApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      `${configJSON.deletePortfolioEndPoint}/${id}/delete_specific_media`
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpDeleteMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleUserPortfolioDeleteResponse = (
    responseJson: IDeletePortfolioSuccessResponse | ICreateProfileErrorResponse
  ) => {
    if (responseJson) {
      this.setState({
        isdeletePortfolioImgOpen: false,
      });
      this.getProfileDetails();
    }
  };

  editUserPortfolio = async (file: File, id: number) => {
    const token = await getStorageData(configJSON.TOKEN);
    const formData = new FormData();
    formData.append("upload_media", file);
    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
    this.editPortfolioApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      `${configJSON.deletePortfolioEndPoint}/${id}/update_specific_media`
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({ token })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestBodyMessage),
      formData
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpPatchMethod
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleUserEditPortfolioResponse = (
    responseJson: ICreateProfileSuccessResponse | ICreateProfileErrorResponse
  ) => {
    if ("data" in responseJson) {
      const mediaData: PortfolioItem[] =
        responseJson.data.attributes.upload_media;
      const updatedMedia: PortfolioItem[] = mediaData.map((item, index) => ({
        id: item.id ?? index,
        type: item.type,
        url: item.url,
      }));
      this.setState(
        {
          portfolioImgs: updatedMedia,
          iseditPortfolioImgOpen: false,
          validPortfolioError: [],
        },
        () => {
          this.getProfileDetails();
        }
      );
    } else {
      this.setState({ validPortfolioError: [] });
    }
  };
  // Customizable Area End
}
